import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Unlink, RefreshCw, CheckCircle2, AlertCircle, Loader2, Download
} from 'lucide-react';
import StravaConnectOrangeButton from '../brand/StravaConnectOrangeButton';
import StravaConnectConsentText from '../brand/StravaConnectConsentText';
import api from '../../utils/axiosConfig';
import { maybeOpenStravaConnectModalFromSyncEvent } from '../../utils/stravaReconnectModal';

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const sec = Math.floor((now - d) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} hr ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)} days ago`;
  return d.toLocaleDateString();
}

const PHASE_LABELS = {
  fetch: 'strava.progress.fetching',
  summary: 'strava.progress.saving',
  gear: 'strava.progress.gear',
  details: 'strava.progress.details',
  native_strava_push: 'strava.progress.nativeUpload',
};

const StravaConnect = ({
  config,
  widget,
  forceDisconnectedPreview = false,
  suppressTopHeader = false,
}) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState(
    forceDisconnectedPreview ? { connected: false } : null
  );
  const [loading, setLoading] = useState(!forceDisconnectedPreview);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [progress, setProgress] = useState(null);
  const [fetchStreamsProcessing, setFetchStreamsProcessing] = useState(false);
  const [fetchStreamsProgress, setFetchStreamsProgress] = useState(null);
  const [fetchStreamsResult, setFetchStreamsResult] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const abortRef = useRef(null);
  const includeDetailsDefaultAppliedRef = useRef(false);

  const needsAppCredentialsForm =
    !!status &&
    !status.connected &&
    status.appCredentialsConfigured === false;

  const [appCredMeta, setAppCredMeta] = useState(null);
  const [credForm, setCredForm] = useState({ clientId: '', clientSecret: '' });
  const [savingCreds, setSavingCreds] = useState(false);
  const [nativeStravaPush, setNativeStravaPush] = useState(false);
  const [savingNativeStravaSettings, setSavingNativeStravaSettings] = useState(false);
  const [sessionShareImageDefault, setSessionShareImageDefault] = useState(false);
  const [sessionShareImageIncludePeople, setSessionShareImageIncludePeople] = useState(false);

  const checkStatus = useCallback(async () => {
    if (forceDisconnectedPreview) return;
    try {
      const response = await api.get('/api/strava/status');
      setStatus(response.data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [forceDisconnectedPreview]);

  const fetchSyncLog = useCallback(async () => {
    try {
      const response = await api.get('/api/strava/sync-log');
      setSyncLogs(response.data.logs ?? []);
    } catch {
      setSyncLogs([]);
    }
  }, []);

  useEffect(() => {
    if (forceDisconnectedPreview) return;
    checkStatus();
  }, [checkStatus, forceDisconnectedPreview]);

  useEffect(() => {
    if (forceDisconnectedPreview) return;
    if (status?.connected) fetchSyncLog();
  }, [status?.connected, fetchSyncLog, forceDisconnectedPreview]);

  useEffect(() => {
    if (forceDisconnectedPreview) return;
    const handler = () => checkStatus();
    window.addEventListener('stravaSyncComplete', handler);
    return () => window.removeEventListener('stravaSyncComplete', handler);
  }, [checkStatus, forceDisconnectedPreview]);

  useEffect(() => {
    if (forceDisconnectedPreview) return;
    if (!status?.connected) {
      includeDetailsDefaultAppliedRef.current = false;
      return;
    }
    if (includeDetailsDefaultAppliedRef.current) return;
    includeDetailsDefaultAppliedRef.current = true;
    if (status.defaultIncludeDetailsRecommended) {
      setIncludeDetails(true);
    }
  }, [status?.connected, status?.defaultIncludeDetailsRecommended, forceDisconnectedPreview]);

  useEffect(() => {
    if (forceDisconnectedPreview || !status?.connected) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/users/settings');
        if (!cancelled) {
          setNativeStravaPush(!!data.zitiusNativeStravaPush);
          setSessionShareImageDefault(data.sessionShareImageDefault === 'on');
          setSessionShareImageIncludePeople(!!data.sessionShareImageIncludePeople);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status?.connected, forceDisconnectedPreview]);

  const saveNativeStravaSettings = async (patch) => {
    setSavingNativeStravaSettings(true);
    try {
      await api.put('/api/users/settings', patch);
    } catch (err) {
      const msg = err.response?.data?.error || t('strava.nativeSessionsSaveFailed');
      setSyncResult({ type: 'error', message: msg });
    } finally {
      setSavingNativeStravaSettings(false);
    }
  };

  useEffect(() => {
    if (!needsAppCredentialsForm || forceDisconnectedPreview) {
      setAppCredMeta(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/strava/app-credentials');
        if (!cancelled) {
          setAppCredMeta(data);
          if (data.clientId) {
            setCredForm((f) => ({ ...f, clientId: data.clientId }));
          }
        }
      } catch {
        if (!cancelled) setAppCredMeta({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsAppCredentialsForm, forceDisconnectedPreview]);

  const handleSaveAppCredentials = async () => {
    setSavingCreds(true);
    setSyncResult(null);
    try {
      await api.put('/api/strava/app-credentials', {
        clientId: credForm.clientId.trim(),
        clientSecret: credForm.clientSecret,
      });
      setCredForm((f) => ({ ...f, clientSecret: '' }));
      await checkStatus();
      setSyncResult({ type: 'success', message: t('strava.appCredentialsSaved') });
    } catch (err) {
      const msg = err.response?.data?.error || t('strava.appCredentialsSaveFailed');
      setSyncResult({ type: 'error', message: msg });
    } finally {
      setSavingCreds(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await api.get('/api/strava/auth-url');
      window.location.href = response.data.url;
    } catch (err) {
      if (err.response?.status === 503) {
        setSyncResult({
          type: 'error',
          message: err.response?.data?.message || t('strava.configRequired'),
        });
      }
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('strava.disconnectConfirm'))) return;
    try {
      await api.delete('/api/strava/disconnect');
      setStatus({ connected: false });
      setSyncResult(null);
    } catch {
      setSyncResult({ type: 'error', message: 'Failed to disconnect' });
    }
  };

  const handleFetchStreams = async () => {
    setFetchStreamsProcessing(true);
    setFetchStreamsProgress(null);
    setFetchStreamsResult(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await api.get('/api/auth/profile');
      const baseUrl = process.env.REACT_APP_API_URL
        || (typeof window !== 'undefined' && window.location.port === '3000' ? 'http://localhost:5001' : '');
      const response = await fetch(`${baseUrl}/api/strava/fetch-streams`, {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
      });
      if (!response.ok) {
        let errMsg = `Fetch failed (${response.status})`;
        try {
          const errData = await response.json();
          errMsg = errData.error || errMsg;
        } catch {
          const text = await response.text();
          if (text) errMsg = text.slice(0, 200);
        }
        throw new Error(errMsg);
      }
      if (!response.body) throw new Error('No response body');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'progress') setFetchStreamsProgress(event);
            else if (event.type === 'complete') {
              const fetched = event.fetched ?? 0;
              const noStream = event.noStream ?? 0;
              let msg;
              if (fetched > 0 && noStream > 0) {
                msg = t('strava.fetchStreamsCompleteMixed', { fetched, noStream });
              } else if (fetched > 0) {
                msg = t('strava.fetchStreamsComplete', { count: fetched });
              } else if (noStream > 0) {
                msg = t('strava.fetchStreamsCompleteNone');
              } else {
                msg = t('strava.fetchStreamsComplete', { count: 0 });
              }
              setFetchStreamsResult({ type: 'success', message: msg });
              setSyncResult({ type: 'success', message: msg });
              checkStatus();
            } else if (event.type === 'error') {
              const msg = event.error || t('strava.fetchStreamsError');
              setFetchStreamsResult({ type: 'error', message: msg });
              setSyncResult({ type: 'error', message: msg });
              maybeOpenStravaConnectModalFromSyncEvent(event);
            }
          } catch { /* skip */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const msg = err.message || t('strava.fetchStreamsError');
        setFetchStreamsResult({ type: 'error', message: msg });
        setSyncResult({ type: 'error', message: msg });
      }
    } finally {
      setFetchStreamsProcessing(false);
      setFetchStreamsProgress(null);
      abortRef.current = null;
    }
  };

  const handleSync = async (incremental = false) => {
    setSyncing(true);
    setSyncResult(null);
    setProgress(null);
    window.dispatchEvent(new Event('stravaSyncStarted'));

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await api.get('/api/auth/profile');

      const response = await fetch('/api/strava/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ includeDetails, overwrite: overwriteMode, incremental }),
        signal: controller.signal,
      });

      if (!response.ok) {
        let errorMsg = `Sync failed (${response.status})`;
        try {
          const errData = await response.json();
          errorMsg = errData.error || errorMsg;
        } catch { /* non-JSON response */ }
        setSyncResult({ type: 'error', message: errorMsg });
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedComplete = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'progress') {
              setProgress(event);
            } else if (event.type === 'complete') {
              receivedComplete = true;
              const parts = [];
              if (event.synced > 0) parts.push(t('strava.syncComplete', { count: event.synced }));
              if (event.skipped > 0) parts.push(t('strava.progress.skipped', { count: event.skipped }));
              if (event.gearSynced > 0) parts.push(t('strava.progress.gearDone', { count: event.gearSynced }));
              if (event.detailsSynced > 0) parts.push(t('strava.syncDetailsDone', { count: event.detailsSynced }));
              setSyncResult({ type: 'success', message: parts.join(' ') || t('strava.syncComplete', { count: 0 }) });
              setProgress(null);
              fetchSyncLog();
              window.dispatchEvent(new Event('stravaSyncComplete'));
              window.dispatchEvent(new Event('trainingUpdated'));
            } else if (event.type === 'error') {
              receivedComplete = true;
              setSyncResult({ type: 'error', message: event.error });
              setProgress(null);
              checkStatus();
              maybeOpenStravaConnectModalFromSyncEvent(event);
            }
          } catch { /* skip malformed events */ }
        }
      }

      if (!receivedComplete) {
        setSyncResult({ type: 'error', message: 'Sync connection closed unexpectedly' });
        setProgress(null);
        checkStatus();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setSyncResult({ type: 'error', message: err.message || 'Sync failed' });
        checkStatus();
      }
      setProgress(null);
    } finally {
      setSyncing(false);
      abortRef.current = null;
      window.dispatchEvent(new Event('stravaSyncEnded'));
    }
  };

  const runsPendingStreams = status?.runsPendingStreams ?? 0;

  if (loading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <Loader2 className='h-6 w-6 animate-spin text-primary' />
      </div>
    );
  }

  const showTopHeader = !(suppressTopHeader && !status?.connected);

  return (
    <div className='h-full p-5 overflow-auto'>
      {showTopHeader && (
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <div className='w-8 h-8 bg-[#FC4C02]/10 rounded-lg flex items-center justify-center'>
              <svg viewBox='0 0 24 24' className='w-5 h-5 fill-[#FC4C02]'>
                <path d='M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169' />
              </svg>
            </div>
            <h3 className='font-semibold text-sm text-foreground'>{t('strava.title')}</h3>
          </div>
          <Badge variant={status?.connected ? 'default' : 'secondary'} className='text-xs'>
            {status?.connected ? t('strava.connected') : t('strava.notConnected')}
          </Badge>
        </div>
      )}

      {status?.connected ? (
        <>
          <div className='bg-muted/50 rounded-lg p-3 mb-3'>
            <div className='flex items-center gap-2'>
              <CheckCircle2 className='h-4 w-4 text-green-500' />
              <div>
                <p className='text-sm font-medium'>
                  {status.athlete?.firstName} {status.athlete?.lastName}
                </p>
                {status.athlete?.city && (
                  <p className='text-xs text-muted-foreground'>
                    {status.athlete.city}, {status.athlete.country}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-2 mb-2'>
            <label className='flex items-center justify-between cursor-pointer group'>
              <span className='text-xs text-muted-foreground group-hover:text-foreground transition-colors'>
                {t('strava.includeDetails')}
              </span>
              <button
                type='button'
                role='switch'
                aria-checked={includeDetails}
                onClick={() => setIncludeDetails(v => !v)}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${includeDetails ? 'bg-primary border-2 border-transparent' : 'bg-muted-foreground/20 border-2 border-muted-foreground/40'}`}
              >
                <span className={`pointer-events-none block h-4 w-4 rounded-full shadow-lg ring-0 transition-transform ${includeDetails ? 'translate-x-4 bg-background' : 'translate-x-0 bg-muted-foreground/70'}`} />
              </button>
            </label>
            <label className='flex items-center justify-between cursor-pointer group'>
              <span className='text-xs text-muted-foreground group-hover:text-foreground transition-colors'>
                {t('strava.overwriteMode')}
              </span>
              <button
                type='button'
                role='switch'
                aria-checked={overwriteMode}
                onClick={() => setOverwriteMode(v => !v)}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${overwriteMode ? 'bg-destructive border-2 border-transparent' : 'bg-muted-foreground/20 border-2 border-muted-foreground/40'}`}
              >
                <span className={`pointer-events-none block h-4 w-4 rounded-full shadow-lg ring-0 transition-transform ${overwriteMode ? 'translate-x-4 bg-background' : 'translate-x-0 bg-muted-foreground/70'}`} />
              </button>
            </label>
          </div>

          <div className='mt-3 pt-3 border-t border-border'>
            <p className='text-xs font-medium text-foreground mb-1'>{t('strava.nativeSessionsSection')}</p>
            {status?.stravaHasActivityWrite === false && (
              <p className='text-xs text-muted-foreground mb-2'>{t('strava.nativeSessionsReconnectForWrite')}</p>
            )}
            <div className='flex flex-col gap-2'>
              <label className={`flex items-center justify-between group ${savingNativeStravaSettings ? 'opacity-70 pointer-events-none' : 'cursor-pointer'}`}>
                <span className='text-xs text-muted-foreground group-hover:text-foreground transition-colors pr-2'>
                  {t('strava.nativeSessionsPush')}
                </span>
                <button
                  type='button'
                  role='switch'
                  aria-checked={nativeStravaPush}
                  disabled={savingNativeStravaSettings}
                  onClick={async () => {
                    const next = !nativeStravaPush;
                    setNativeStravaPush(next);
                    await saveNativeStravaSettings({ zitiusNativeStravaPush: next });
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${nativeStravaPush ? 'bg-primary border-2 border-transparent' : 'bg-muted-foreground/20 border-2 border-muted-foreground/40'}`}
                >
                  <span className={`pointer-events-none block h-4 w-4 rounded-full shadow-lg ring-0 transition-transform ${nativeStravaPush ? 'translate-x-4 bg-background' : 'translate-x-0 bg-muted-foreground/70'}`} />
                </button>
              </label>
              <p className='text-[11px] text-muted-foreground leading-snug -mt-0.5'>{t('strava.nativeSessionsPushHint')}</p>
              <label className={`flex items-center justify-between group mt-2 pt-2 border-t border-border ${savingNativeStravaSettings ? 'opacity-70 pointer-events-none' : 'cursor-pointer'}`}>
                <span className='text-xs text-muted-foreground group-hover:text-foreground transition-colors pr-2'>
                  {t('strava.sessionShareImageDefault')}
                </span>
                <button
                  type='button'
                  role='switch'
                  aria-checked={sessionShareImageDefault}
                  disabled={savingNativeStravaSettings}
                  onClick={async () => {
                    const next = !sessionShareImageDefault;
                    setSessionShareImageDefault(next);
                    await saveNativeStravaSettings({ sessionShareImageDefault: next ? 'on' : 'off' });
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${sessionShareImageDefault ? 'bg-primary border-2 border-transparent' : 'bg-muted-foreground/20 border-2 border-muted-foreground/40'}`}
                >
                  <span className={`pointer-events-none block h-4 w-4 rounded-full shadow-lg ring-0 transition-transform ${sessionShareImageDefault ? 'translate-x-4 bg-background' : 'translate-x-0 bg-muted-foreground/70'}`} />
                </button>
              </label>
              <p className='text-[11px] text-muted-foreground leading-snug'>{t('strava.sessionShareImageDefaultHint')}</p>
              <label className={`flex items-center justify-between group ${savingNativeStravaSettings ? 'opacity-70 pointer-events-none' : 'cursor-pointer'}`}>
                <span className='text-xs text-muted-foreground group-hover:text-foreground transition-colors pr-2'>
                  {t('strava.sessionShareImageIncludePeople')}
                </span>
                <button
                  type='button'
                  role='switch'
                  aria-checked={sessionShareImageIncludePeople}
                  disabled={savingNativeStravaSettings}
                  onClick={async () => {
                    const next = !sessionShareImageIncludePeople;
                    setSessionShareImageIncludePeople(next);
                    await saveNativeStravaSettings({ sessionShareImageIncludePeople: next });
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${sessionShareImageIncludePeople ? 'bg-primary border-2 border-transparent' : 'bg-muted-foreground/20 border-2 border-muted-foreground/40'}`}
                >
                  <span className={`pointer-events-none block h-4 w-4 rounded-full shadow-lg ring-0 transition-transform ${sessionShareImageIncludePeople ? 'translate-x-4 bg-background' : 'translate-x-0 bg-muted-foreground/70'}`} />
                </button>
              </label>
              <p className='text-[11px] text-muted-foreground leading-snug'>{t('strava.sessionShareImageIncludePeopleHint')}</p>
            </div>
          </div>

          <div className='flex flex-col gap-2'>
            <Button
              onClick={() => handleSync(false)}
              disabled={syncing}
              size='sm'
              className='w-full'
            >
              {syncing ? (
                <Loader2 className='h-4 w-4 animate-spin mr-2' />
              ) : (
                <RefreshCw className='h-4 w-4 mr-2' />
              )}
              {syncing ? t('strava.syncing') : t('strava.syncFull')}
            </Button>

            {syncLogs.length > 0 && (
              <p className='text-xs text-muted-foreground'>
                {t('strava.lastSyncDetail', {
                  count: syncLogs[0].activities_synced ?? 0,
                  type: syncLogs[0].sync_type === 'incremental' ? t('strava.syncTypeIncremental') : t('strava.syncTypeFull'),
                })}
                {syncLogs[0].completed_at && (
                  <> · {formatRelativeTime(syncLogs[0].completed_at)}</>
                )}
              </p>
            )}

            <Button
              variant='outline'
              size='sm'
              className='w-full text-destructive hover:text-destructive'
              onClick={handleDisconnect}
            >
              <Unlink className='h-4 w-4 mr-2' />
              {t('strava.disconnect')}
            </Button>
          </div>

          {runsPendingStreams > 0 && (
            <div className='mt-3 pt-3 border-t border-border'>
              <div className='flex items-center gap-2 mb-2'>
                <Download className='h-4 w-4 text-muted-foreground' />
                <span className='text-xs font-medium text-foreground'>{t('strava.streamsSection')}</span>
              </div>
              <p className='text-xs text-muted-foreground mb-2'>
                {t('strava.runsPendingStreams', { count: runsPendingStreams })}
              </p>
              <Button
                variant='outline'
                size='sm'
                className='w-full mb-2'
                disabled={fetchStreamsProcessing}
                onClick={handleFetchStreams}
              >
                {fetchStreamsProcessing ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : (
                  <Download className='h-4 w-4 mr-2' />
                )}
                {fetchStreamsProcessing ? t('strava.fetchStreamsProcessing') : t('strava.fetchStreams')}
              </Button>
              {fetchStreamsProcessing && (
                <div className='mb-2'>
                  {(fetchStreamsProgress && fetchStreamsProgress.total != null) ? (
                    <>
                      <div className='h-1.5 w-full bg-muted rounded-full overflow-hidden'>
                        <div
                          className='h-full bg-primary rounded-full transition-all'
                          style={{ width: `${Math.max(fetchStreamsProgress.percent ?? 0, 2)}%` }}
                        />
                      </div>
                      <p className='text-xs text-muted-foreground mt-1'>
                        {fetchStreamsProgress.current} / {fetchStreamsProgress.total}
                      </p>
                    </>
                  ) : (
                    <p className='text-xs text-muted-foreground'>{t('strava.streamsConnecting')}</p>
                  )}
                </div>
              )}
              {fetchStreamsResult && (
                <div className={`mb-2 flex items-start gap-2 p-2 rounded text-xs ${
                  fetchStreamsResult.type === 'success'
                    ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                    : 'bg-destructive/10 text-destructive'
                }`}>
                  {fetchStreamsResult.type === 'success'
                    ? <CheckCircle2 className='h-3.5 w-3.5 mt-0.5 shrink-0' />
                    : <AlertCircle className='h-3.5 w-3.5 mt-0.5 shrink-0' />
                  }
                  <span>{fetchStreamsResult.message}</span>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {needsAppCredentialsForm ? (
            <>
              <p className='text-sm text-muted-foreground mb-3'>
                {t('strava.appCredentialsIntro')}
              </p>
              {!!appCredMeta?.redirectUri && (
                <div className='mb-4 p-3 rounded-lg bg-muted/50 border border-border'>
                  <p className='text-xs font-medium text-foreground mb-1'>
                    {t('strava.appCredentialsRedirectUri')}
                  </p>
                  <code className='text-xs break-all text-muted-foreground block'>
                    {appCredMeta.redirectUri}
                  </code>
                  <p className='text-xs text-muted-foreground mt-2'>
                    {t('strava.appCredentialsRedirectUriHint')}
                  </p>
                </div>
              )}
              <div className='space-y-3 mb-4'>
                <div className='space-y-1.5'>
                  <Label htmlFor='strava-app-client-id'>{t('strava.appCredentialsClientId')}</Label>
                  <Input
                    id='strava-app-client-id'
                    autoComplete='off'
                    value={credForm.clientId}
                    onChange={(e) => setCredForm((f) => ({ ...f, clientId: e.target.value }))}
                    placeholder={t('strava.appCredentialsClientId')}
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label htmlFor='strava-app-client-secret'>{t('strava.appCredentialsClientSecret')}</Label>
                  <Input
                    id='strava-app-client-secret'
                    type='password'
                    autoComplete='new-password'
                    value={credForm.clientSecret}
                    onChange={(e) => setCredForm((f) => ({ ...f, clientSecret: e.target.value }))}
                    placeholder={t('strava.appCredentialsClientSecret')}
                  />
                </div>
                <Button
                  type='button'
                  size='sm'
                  className='w-full'
                  disabled={savingCreds || !credForm.clientId.trim() || !credForm.clientSecret}
                  onClick={handleSaveAppCredentials}
                >
                  {savingCreds ? (
                    <Loader2 className='h-4 w-4 animate-spin mr-2' />
                  ) : null}
                  {t('strava.appCredentialsSave')}
                </Button>
              </div>
            </>
          ) : (
            <p className='text-sm text-muted-foreground mb-3'>
              {t('strava.connectDescription')}
            </p>
          )}

          {!needsAppCredentialsForm && (
            <>
              <StravaConnectConsentText className='mb-4' />

              <div className='flex justify-center w-full'>
                <StravaConnectOrangeButton
                  onClick={handleConnect}
                  className='w-full max-w-[min(100%,20rem)] justify-center'
                  imgClassName='w-full h-auto max-h-12 sm:max-h-14 object-contain'
                />
              </div>
            </>
          )}
        </>
      )}

      {progress && (
        <div className='mt-3'>
          <div className='flex items-center justify-between mb-1'>
            <span className='text-xs text-muted-foreground'>
              {t(PHASE_LABELS[progress.phase] || 'strava.syncing')}
            </span>
            <span className='text-xs font-medium text-foreground'>
              {progress.percent != null ? `${progress.percent}%` : ''}
              {progress.current != null && progress.total != null
                ? ` (${progress.current}/${progress.total})`
                : progress.current != null ? ` (${progress.current})` : ''}
            </span>
          </div>
          <div className='h-2 w-full bg-muted rounded-full overflow-hidden'>
            <div
              className='h-full bg-[#FC4C02] rounded-full transition-all duration-300 ease-out'
              style={{ width: `${progress.percent ?? 0}%` }}
            />
          </div>
          {progress.message && (
            <p className='text-xs text-muted-foreground mt-1'>{progress.message}</p>
          )}
        </div>
      )}

      {syncResult && (
        <>
          <Separator className='my-3' />
          <div className={`flex items-start gap-2 p-2 rounded text-xs ${
            syncResult.type === 'success'
              ? 'bg-green-500/10 text-green-700 dark:text-green-400'
              : 'bg-destructive/10 text-destructive'
          }`}>
            {syncResult.type === 'success'
              ? <CheckCircle2 className='h-3.5 w-3.5 mt-0.5 shrink-0' />
              : <AlertCircle className='h-3.5 w-3.5 mt-0.5 shrink-0' />
            }
            <span>{syncResult.message}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default StravaConnect;
